import React from 'react';
import CandidateCardSkeleton from './CandidateCardSkeleton';

/**
 * AIMatchingSkeleton - Comprehensive skeleton loading for AI matching sections
 * 
 * @param {string} section - Which section to render skeleton for: 'recommendations', 'smart-search', 'insights', 'predictions'
 * @param {number} itemCount - Number of skeleton items to show
 */
const AIMatchingSkeleton = ({ section = 'recommendations', itemCount = 3 }) => {
  
  const _RecommendationsSkeleton = () => (
    <div className="space-y-6">
      {/* Header Stats Skeleton */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-6 border border-primary-200 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
            <div>
              <div className="w-48 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
              <div className="w-64 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
          </div>
          <div className="w-20 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="text-center">
              <div className="w-12 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded mx-auto mb-1"></div>
              <div className="w-24 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mx-auto"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Sort and Filter Controls Skeleton */}
      <div className="bg-surface rounded-lg border border-border p-4 animate-pulse">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="w-80 h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg"></div>
          <div className="flex items-center space-x-3">
            <div className="w-32 h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg"></div>
            <div className="w-24 h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg"></div>
          </div>
        </div>
      </div>

      {/* Candidate Cards Skeleton */}
      <div className="space-y-6">
        {Array.from({ length: itemCount }).map((_, index) => (
          <CandidateCardSkeleton key={index} delay={index * 100} />
        ))}
      </div>
    </div>
  );

  const _SmartSearchSkeleton = () => (
    <div className="space-y-6">
      {/* AI-Enhanced Search Filters Skeleton */}
      <div className="bg-surface rounded-lg border border-border p-6 animate-pulse">
        <div className="w-48 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i}>
              <div className="w-40 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
              <div className="w-full h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg mb-1"></div>
              <div className="w-32 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Market Insights Skeleton */}
      <div className="bg-surface rounded-lg border border-border p-6 animate-pulse">
        <div className="w-32 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="text-center p-4 bg-secondary-50 rounded-lg">
              <div className="w-12 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded mx-auto mb-1"></div>
              <div className="w-24 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mx-auto"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const _InsightsSkeleton = () => (
    <div className="space-y-6">
      {/* Optimization Suggestions Skeleton */}
      <div className="bg-surface rounded-lg border border-border p-6 animate-pulse">
        <div className="w-44 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start space-x-4 p-4 bg-secondary-50 rounded-lg">
              <div className="w-8 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full flex-shrink-0"></div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-24 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                    <div className="w-16 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                  </div>
                </div>
                <div className="w-full h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Success Factors Analysis Skeleton */}
      <div className="bg-surface rounded-lg border border-border p-6 animate-pulse">
        <div className="w-48 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="w-40 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                <div className="w-12 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex-1 h-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                <div className="w-16 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const _PredictionsSkeleton = () => (
    <div className="space-y-6">
      {/* 30-Day Hiring Forecast Skeleton */}
      <div className="bg-surface rounded-lg border border-border p-6 animate-pulse">
        <div className="w-44 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="w-12 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded mx-auto mb-1"></div>
              <div className="w-32 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mx-auto"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Seasonal Trends Skeleton */}
      <div className="bg-surface rounded-lg border border-border p-6 animate-pulse">
        <div className="w-32 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4"></div>
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-6 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            <div>
              <div className="w-24 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-1"></div>
              <div className="w-48 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i}>
                <div className="w-20 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-1"></div>
                <div className="w-16 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Render appropriate skeleton based on section
  switch (section) {
    case 'recommendations':
      return <_RecommendationsSkeleton />;
    case 'smart-search':
      return <_SmartSearchSkeleton />;
    case 'insights':
      return <_InsightsSkeleton />;
    case 'predictions':
      return <_PredictionsSkeleton />;
    default:
      return <_RecommendationsSkeleton />;
  }
};

export default AIMatchingSkeleton;
